// Generic, product-agnostic integration snippets shown in the Integrations > Instructions tab.
// The merchant replaces TU_ID_DE_PRODUCTO with each product's Customizer ID (copied from the
// product screen) and TU-TIENDA with their store address. Works on Wix, Shopify, WordPress
// or any website that lets you paste HTML.
export function buildSnippets(origin) {
  const O = origin || "https://hephy3d.com";
  const HOST = (() => { try { return new URL(O).hostname; } catch (e) { return "hephy3d.com"; } })();

  // 1) Universal embed (iframe) — paste into any HTML block (WordPress, Shopify, custom site).
  const embedCode = `<iframe src="${O}/embed/TU_ID_DE_PRODUCTO" width="100%" height="760" frameborder="0" style="border:0;border-radius:16px"></iframe>`;

  // 2) Universal "Personalizar" button — opens the customizer in a popup and lets the customer
  //    design + pay in Hephy. Works on ANY platform (WordPress, Squarespace, plain HTML, etc.).
  const universalButton = `<!-- Botón "Personalizar" universal (WordPress, HTML, cualquier web).
     El cliente diseña, PAGA en Hephy y la orden entra sola a tu tienda por API. -->
<a href="${O}/embed/TU_ID_DE_PRODUCTO?store=TU-TIENDA"
   onclick="window.open(this.href,'hephy','width=1200,height=900');return false;"
   style="display:inline-block;background:#111;color:#fff;text-decoration:none;border-radius:999px;padding:14px 34px;font:600 15px system-ui,sans-serif">
   Personalizar en 3D
</a>
<!-- Para atribuir la venta a un vendedor, comparte el enlace con: &rep=NOMBRE&rep_tel=+1787XXXXXXX -->`;

  // Wix Option D button HTML (popup, more control), placed inside an "Embed HTML" (Code mode) block.
  // Starts HIDDEN and only reveals itself when Velo sends the Hephy ID for the current product,
  // so the button NEVER appears on products that don't have a customizer.
  const popupButtonCode = `<!-- Insertar → Insertar HTML (modo "Código"), ID del elemento: hephyButton.
     Marca el elemento como "Colapsado/Oculto al cargar" en Wix. Solo se mostrará en productos
     que tengan personalizador (los que guardaste su ID de Hephy en el campo SKU). -->
<div id="hephyWrap" style="text-align:center;padding:10px;display:none">
  <button id="hephyBtn" style="background:#111;color:#fff;border:0;border-radius:999px;padding:13px 30px;font-size:15px;font-weight:600;cursor:pointer">Personalizar en 3D</button>
</div>
<script>
(function(){
  var ORIGIN = '${O}', HEPHY_ID = null, pop = null;
  var wrap = document.getElementById('hephyWrap'), btn = document.getElementById('hephyBtn');
  btn.addEventListener('click', function(){
    if (!HEPHY_ID) return;
    pop = window.open(ORIGIN + '/embed/' + HEPHY_ID + '?popup=1', 'hephyCustomizer', 'width=1180,height=880');
  });
  window.addEventListener('message', function(e){
    var d = e.data;
    if (typeof d === 'string'){ try { d = JSON.parse(d); } catch(_){ return; } }
    if (!d || !d.type) return;
    if (d.type === 'HEPHY_SET_ID'){ HEPHY_ID = d.id || null; wrap.style.display = HEPHY_ID ? 'block' : 'none'; }  // Velo -> mostrar/ocultar botón
    else if (d.type === 'ADD_TO_WIX_CART'){ window.parent.postMessage(JSON.stringify(d), '*'); }                  // popup -> Velo
    else if (d.type === 'ADD_TO_WIX_CART_SUCCESS' || d.type === 'ADD_TO_WIX_CART_ERROR'){                          // Velo -> popup
      if (pop){ try { pop.postMessage(JSON.stringify(d), '*'); } catch(_){} }
    }
  });
  try { window.parent.postMessage(JSON.stringify({ type:'HEPHY_READY' }), '*'); } catch(_){}   // avisa a Velo que ya puede enviar el ID
})();
</script>`;

  // Wix Option C relay HTML (inline customizer), inside an "Embed HTML" (Code mode) block, ID hephyEmbed.
  const embedRelayHtml = `<!-- Insertar → Insertar HTML en modo "CÓDIGO" · ID del elemento: hephyEmbed -->
<iframe id="hephyFrame" src="about:blank" style="width:100%;height:760px;border:0;border-radius:16px" allow="autoplay; fullscreen"></iframe>
<script>
(function(){
  var ORIGIN = '${O}';
  var frame = document.getElementById('hephyFrame');
  var loaded = false;
  function ping(){ if (loaded) return; try { window.parent.postMessage(JSON.stringify({ type:'HEPHY_READY' }), '*'); } catch(_){} }
  var t = setInterval(ping, 500); ping();                 // reintenta avisar "listo" (evita carrera de tiempos)
  setTimeout(function(){ clearInterval(t); }, 8000);
  window.addEventListener('message', function(e){
    var d = e.data;
    if (typeof d === 'string'){ try { d = JSON.parse(d); } catch(_){ return; } }
    if (!d || !d.type) return;
    if (d.type === 'HEPHY_LOAD' && d.id){ loaded = true; clearInterval(t); frame.src = ORIGIN + '/embed/' + d.id; }  // Velo -> cargar producto
    else if (d.type === 'ADD_TO_WIX_CART'){ window.parent.postMessage(JSON.stringify(d), '*'); }    // personalizador -> Velo
    else if (d.type === 'ADD_TO_WIX_CART_SUCCESS' || d.type === 'ADD_TO_WIX_CART_ERROR'){            // Velo -> personalizador
      if (frame.contentWindow) frame.contentWindow.postMessage(JSON.stringify(d), '*');
    }
  });
})();
</script>`;

  // Wix Option C Velo code (Product Page + backend web module).
  const veloCode = `// === CÓDIGO DE LA PÁGINA DE PRODUCTO (Velo / Dev Mode) — RELÉ inline ===
// IMPORTANTE: usa un elemento "Insertar → Insertar HTML" en modo "CÓDIGO" (NO "Dirección web/URL"),
// con ID EXACTO: hephyEmbed, y pega dentro el HTML del relé (recuadro de arriba).
// (Los elementos en modo "Dirección web/URL" NO soportan onMessage → por eso el carrito fallaba.)
// En el campo SKU de CADA producto de Wix, guarda el ID de su personalizador Hephy 3D.
import { getVariantSku } from 'backend/customCart.web';
import wixLocationFrontend from 'wix-location-frontend';
import { cart } from 'wix-stores-frontend';

// Título EXACTO del "Campo de texto personalizado" que definiste en el producto de Wix.
const HEPHY_FIELD_TITLE = 'Detalles del diseño';
// Nombre EXACTO de la OPCIÓN de producto en Wix que define el precio por metal (ej. 'Metal').
// Deja '' si tus productos NO tienen opción de metal con precios (usará el precio base).
const HEPHY_METAL_OPTION = 'Metal';

function msgOk(el){ return el && typeof el.onMessage === 'function' && typeof el.postMessage === 'function'; }

$w.onReady(async () => {
  await loadCustomizer();
  wixLocationFrontend.onChange(async () => {
    await new Promise(r => setTimeout(r, 300));
    await loadCustomizer();
  });
  const el = $w('#hephyEmbed');
  if (msgOk(el)) {
    el.onMessage(async (event) => {
      let msg = event && event.data;
      if (typeof msg === 'string') { try { msg = JSON.parse(msg); } catch (e) { return; } }
      if (!msg) return;
      if (msg.type === 'HEPHY_READY') { await loadCustomizer(); return; }
      if (msg.type !== 'ADD_TO_WIX_CART') return;
      try {
        const product = await $w('#productPage1').getProduct();
        if (!product || !product._id) throw new Error('Producto de Wix no detectado (revisa #productPage1)');
        const fields = (msg.payload && msg.payload.customTextFields) || [];
        const resumen = (fields.map(function(f){ return (f.title + ': ' + f.value); }).join('  ·  ').slice(0, 900))
          || ((msg.payload && msg.payload.customizationText) || 'Diseño personalizado');
        const options = { customTextFields: [{ title: HEPHY_FIELD_TITLE, value: resumen }] };
        if (HEPHY_METAL_OPTION) {
          const mf = fields.find(function(f){ return /metal/i.test(f.title || ''); });
          if (mf && mf.value) { options.choices = {}; options.choices[HEPHY_METAL_OPTION] = String(mf.value).trim(); }
        }
        await cart.addProducts([{ productId: product._id, quantity: 1, options: options }]);
        el.postMessage({ type: 'ADD_TO_WIX_CART_SUCCESS' });
      } catch (e) {
        el.postMessage({ type: 'ADD_TO_WIX_CART_ERROR', error: String((e && e.message) || e) });
      }
    });
  }
});

async function loadCustomizer() {
  let product;
  try { product = await $w('#productPage1').getProduct(); }
  catch (e) { return; }
  let id = product && product.sku;
  if (!id && product && product._id) {
    try { id = await getVariantSku(product._id); } catch (e) {}
  }
  const el = $w('#hephyEmbed');
  if (!el) return;
  if (!id) { if (typeof el.collapse === 'function') el.collapse(); return; }
  if (typeof el.expand === 'function') el.expand();
  if (typeof el.postMessage === 'function') {
    el.postMessage({ type: 'HEPHY_LOAD', id: id });
  } else {
    try { el.src = '${O}/embed/' + id; } catch (e) {}
  }
}

// === ARCHIVO BACKEND: backend/customCart.web.js ===
import { Permissions, webMethod } from 'wix-web-module';
import { currentCart } from 'wix-ecom-backend';
import { getProductVariants } from 'wix-stores-backend';

const WIX_STORES_APP_ID = '215238eb-22a5-4c36-9e7b-e7c08025e04e'; // ID fijo de Wix Stores

export const getVariantSku = webMethod(Permissions.Anyone, async (productId) => {
  try {
    const res = await getProductVariants(productId);
    const items = (res && res.items) || [];
    for (const it of items) {
      const sku = it && it.variant && it.variant.sku;
      if (sku) return String(sku).trim();
    }
  } catch (e) {}
  return null;
});

export const addCustomizedItemToCart = webMethod(Permissions.Anyone, async (payload) => {
  const catalogItemId = payload.wixProductId;
  const raw = (payload.customTextFields && payload.customTextFields.length)
    ? payload.customTextFields
    : [{ title: 'Personalización', value: payload.customizationText || '' }];
  const fields = raw.filter(f => f && f.title).slice(0, 10)
    .map(f => ({ title: String(f.title).slice(0, 100), value: String(f.value == null ? '' : f.value).slice(0, 400) }));
  const options = { customTextFields: fields };
  try {
    const res = await getProductVariants(catalogItemId);
    const variants = (res && res.items) || [];
    if (variants.length) {
      const wantVals = fields.map(f => String(f.value).toLowerCase().trim()).filter(Boolean);
      let match = null;
      if (wantVals.length) {
        match = variants.find(v => {
          const vals = Object.values((v && v.choices) || {}).map(x => String(x).toLowerCase().trim());
          return vals.length && vals.every(val => wantVals.indexOf(val) !== -1);
        });
      }
      const chosen = match || variants[0];
      const vId = chosen && (chosen._id || (chosen.variant && chosen.variant._id));
      if (vId) options.variantId = vId;
    }
  } catch (e) {}
  return currentCart.addToCurrentCart({
    lineItems: [{ catalogReference: { appId: WIX_STORES_APP_ID, catalogItemId: catalogItemId, options: options }, quantity: 1 }]
  });
});`;

  // Wix Option D Velo code (button popup). Shows the button ONLY on products that have a
  // customizer (SKU = Hephy ID); collapses it everywhere else. Adds a CUSTOM LINE ITEM to the
  // visitor's cart with the calculated price AND the 3D design image (imported to Media Manager),
  // then refreshes the cart so it shows immediately.
  const popupVeloCode = `// === CÓDIGO DE LA PÁGINA DE PRODUCTO (Velo / Dev Mode) — BOTÓN "Personalizar" (solo si aplica) ===
// El elemento HTML (modo "Código") con el botón debe tener el ID: hephyButton
// En el SKU de CADA producto con personalizador guarda el ID de Hephy (si no tiene, el botón no aparece).
// Requiere: backend/media.web.js (importa la foto del diseño) y backend/customCart.web.js (getVariantSku).
// Permisos de la App en Wix Dev Center: "Manage eCommerce – Admin".
import { currentCart } from 'wix-ecom-backend';
import wixEcomFrontend from 'wix-ecom-frontend';
import { importHephySnapshot } from 'backend/media.web';
import { getVariantSku } from 'backend/customCart.web';
import wixLocationFrontend from 'wix-location-frontend';

async function hephyIdForCurrentProduct(){
  let product; try { product = await $w('#productPage1').getProduct(); } catch (e) { return null; }
  let id = product && product.sku;
  if (!id && product && product._id) { try { id = await getVariantSku(product._id); } catch (e) {} }
  return id || null;
}

async function refreshButton(){
  const el = $w('#hephyButton'); if (!el) return;
  const id = await hephyIdForCurrentProduct();
  if (!id) { if (typeof el.collapse === 'function') el.collapse(); return; }   // sin personalizador -> ocultar
  if (typeof el.expand === 'function') el.expand();
  if (typeof el.postMessage === 'function') el.postMessage({ type: 'HEPHY_SET_ID', id: id });
}

$w.onReady(async () => {
  const el = $w('#hephyButton');
  if (el && typeof el.onMessage === 'function') {
    el.onMessage(async (event) => {
      let msg = event.data;
      if (typeof msg === 'string') { try { msg = JSON.parse(msg); } catch (e) { return; } }
      if (!msg) return;
      if (msg.type === 'HEPHY_READY') { await refreshButton(); return; }
      if (msg.type !== 'ADD_TO_WIX_CART') return;
      const p = msg.payload || {};
      try {
        const lines = (p.customTextFields || []).filter(function(f){ return f && f.value; })
          .map(function(f){ return { name: { original: String(f.title).slice(0,80) }, plainText: { original: String(f.value).slice(0,500) } }; });
        const item = {
          quantity: p.quantity || 1,
          price: Number(p.price || 0).toFixed(2),                                  // subtotal con descuento, SIN tax/envío
          productName: { original: String(p.productName || 'Personalización 3D').slice(0,120) },
          itemType: { preset: 'PHYSICAL' },
          descriptionLines: lines
        };
        // Foto del diseño en el carrito: importar al Media Manager y usar el URI wix:image://
        const snap = p.renderImageUrl || p.snapshotUrl;
        if (snap) { try { item.media = await importHephySnapshot(snap); } catch (e) { console.warn('Hephy: foto no importada', e); } }
        await currentCart.addToCurrentCart({ customLineItems: [item] });
        await wixEcomFrontend.refreshCart();
        el.postMessage({ type: 'ADD_TO_WIX_CART_SUCCESS' });
      } catch (e) {
        el.postMessage({ type: 'ADD_TO_WIX_CART_ERROR', error: String((e && e.message) || e) });
      }
    });
  }
  await refreshButton();
  wixLocationFrontend.onChange(async () => { await new Promise(r => setTimeout(r, 300)); await refreshButton(); });
});

// === ARCHIVO BACKEND 1: backend/customCart.web.js (getVariantSku, el MISMO de la Opción C) ===
// === ARCHIVO BACKEND 2: backend/media.web.js (importa la foto del diseño — ver recuadro siguiente) ===`;

  // Backend media importer for the design snapshot (used by Option C & D to show the image in cart).
  const mediaImporterCode = `// === ARCHIVO BACKEND: backend/media.web.js — importa la foto del diseño 3D al Media Manager ===
// Wix NO muestra una URL externa como imagen del carrito: hay que importarla y usar el URI wix:image://
import { Permissions, webMethod } from 'wix-web-module';
import { files } from 'wix-media.v2';
import { elevate } from 'wix-auth';

const importFile = elevate(files.importFile);
const getFileDescriptor = elevate(files.getFileDescriptor);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const importHephySnapshot = webMethod(Permissions.Anyone, async (snapshotUrl) => {
  let url; try { url = new URL(snapshotUrl); } catch (e) { throw new Error('URL inválida'); }
  if (url.protocol !== 'https:') throw new Error('La URL debe ser https');
  if (url.hostname !== '${HOST}') throw new Error('Host no permitido');   // seguridad: solo tu backend Hephy
  const imported = await importFile(url.toString(), { displayName: 'hephy-' + Date.now() + '.png', mediaType: 'IMAGE', mimeType: 'image/png' });
  const fileId = imported && imported.file && imported.file._id;
  if (!fileId) throw new Error('Wix no devolvió el ID del archivo importado');
  for (let i = 0; i < 20; i++) {                     // la importación es asíncrona: esperar READY
    const d = await getFileDescriptor(fileId);
    if (d.operationStatus === 'READY' && d.media && d.media.image && d.media.image.image) return d.media.image.image;
    if (d.operationStatus === 'FAILED') throw new Error('Falló la importación en Wix');
    await sleep(500);
  }
  throw new Error('La imagen sigue procesándose; reintenta');
});`;

  // Site-wide Velo (masterPage.js): reads ?promo= (and ?rep=) from the seller's promo link and
  // forwards it to every embedded Hephy customizer so the discount auto-applies.
  const promoAutoApplyCode = `// === masterPage.js (Velo / Dev Mode) — auto-aplicar el código promo del vendedor ===
// Cuando un cliente llega por el enlace del vendedor (https://TU-DOMINIO/?promo=CODIGO&rep=NOMBRE),
// guarda el promo y se lo pasa al personalizador Hephy embebido para que aplique el descuento solo.
import wixLocationFrontend from 'wix-location-frontend';
import { session } from 'wix-storage-frontend';

$w.onReady(() => {
  const q = wixLocationFrontend.query || {};
  if (q.promo) session.setItem('hephy_promo', String(q.promo));
  if (q.rep)   session.setItem('hephy_rep', String(q.rep));
  const promo = session.getItem('hephy_promo');
  if (!promo) return;
  // Reenvía el promo a cualquier iframe de Hephy (relé Opción C con ID hephyEmbed).
  const el = $w('#hephyEmbed');
  if (el && typeof el.postMessage === 'function') {
    el.postMessage({ type: 'HEPHY_PROMO', promo: promo, rep: session.getItem('hephy_rep') || '' });
  }
});`;

  // Editor-side button (Dashboard/Velo backend): link a Wix product to a Hephy customizer by
  // writing the Hephy product ID into the Wix product's SKU — so "Personalizar" shows for it.
  const dashboardLinkCode = `// === BOTÓN "Personalizar con Hephy 3D" al EDITAR un producto (Velo / Dashboard) ===
// Enlaza un producto de Wix con un personalizador de Hephy escribiendo el ID de Hephy en el SKU
// del producto. Después el botón "Personalizar" aparece solo en ese producto (usa el SKU).
//
// 1) Añade una página de Dashboard (Velo Dev Mode) con un input #hephyIdInput, un texto #wixProductId
//    y un botón #linkBtn.  2) Pega este backend + el código de página.
//
// --- backend/hephyLink.web.js ---
import { Permissions, webMethod } from 'wix-web-module';
import { elevate } from 'wix-auth';
import { products } from 'wix-stores.v2';   // API de catálogo (v2/v3 según tu sitio)

export const setHephyId = webMethod(Permissions.Admin, async (wixProductId, hephyId) => {
  const update = { sku: String(hephyId).trim() };
  return elevate(products.updateProduct)(wixProductId, update);
});

// --- Código de la página de Dashboard ---
import { setHephyId } from 'backend/hephyLink.web';
$w.onReady(() => {
  $w('#linkBtn').onClick(async () => {
    const wixProductId = $w('#wixProductId').text;      // el _id del producto que editas
    const hephyId = $w('#hephyIdInput').value;          // pega el ID del personalizador de Hephy
    if (!wixProductId || !hephyId) return;
    try {
      await setHephyId(wixProductId, hephyId);
      $w('#status').text = '✓ Producto enlazado: se personalizará con Hephy 3D';
    } catch (e) { $w('#status').text = 'Error: ' + (e.message || e); }
  });
});`;

  // Seller portal embedded on the merchant's own Wix domain (iframe page).
  const sellerPageEmbed = `<!-- Página "Vendedores" en tu sitio Wix (Insertar → Insertar HTML, modo "Código").
     Muestra el Portal del Vendedor con TU dominio. Los vendedores entran con su correo/contraseña. -->
<iframe src="${O}/vendedor" width="100%" height="1000" frameborder="0"
        style="border:0;border-radius:16px" allow="clipboard-write"></iframe>`;

  return { embedCode, universalButton, popupButtonCode, embedRelayHtml, veloCode, popupVeloCode, mediaImporterCode,
           promoAutoApplyCode, dashboardLinkCode, sellerPageEmbed };
}
